<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="docrepo-page">
    <div class="page-header">
      <div class="page-avatar"><v-icon size="22" color="#fff">mdi-folder-multiple</v-icon></div>
      <div class="page-header-text">
        <h1 class="page-title">Repozytorium dokumentów</h1>
      </div>
      <button v-if="mainTab === 0" class="action-pill action-pill--secondary mr-2" :disabled="downloadingZip" @click="downloadZip">
        <v-icon size="14">{{ downloadingZip ? 'mdi-loading mdi-spin' : 'mdi-folder-zip' }}</v-icon>
        {{ activeCategory === 0 ? 'Pobierz wszystko (ZIP)' : 'Pobierz kategorię (ZIP)' }}
      </button>
      <button v-if="mainTab === 0" class="action-pill action-pill--primary" @click="uploadDialog = true">
        <v-icon size="14">mdi-upload</v-icon>{{ $t('documentsRepository.addDocument') }}
      </button>
    </div>
    <v-card outlined class="rounded-lg" style="overflow: hidden;">
      <v-card-title>
        <v-icon left>mdi-folder-multiple</v-icon>
        {{ $t('documentsRepository.title') }}
      </v-card-title>
      <v-card-text>
        <v-tabs v-model="mainTab" class="mb-4" background-color="transparent">
          <v-tab v-if="canAccess('company')">
            <v-icon left small>mdi-folder-outline</v-icon>
            {{ $t('documentsRepository.company') }} ({{ documents.length }})
          </v-tab>
          <v-tab v-if="canAccess('signed-by-drivers')">
            <v-icon left small>mdi-account-multiple</v-icon>
            {{ $t('documentsRepository.signedByDrivers') }} ({{ signedDriversTotal }})
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-email-arrow-left-outline</v-icon>
            Skrzynka mailowa
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-folder-lock-outline</v-icon>
            Wewnętrzne kierowców<span v-if="internalCount !== null">&nbsp;({{ internalCount }})</span>
          </v-tab>
        </v-tabs>

        <!-- ========== TAB 1: Firmowe ========== -->
        <template v-if="mainTab === 0 && canAccess('company')">
        <v-text-field
          v-model="search"
          :label="$t('documentsRepository.searchDocuments')"
          prepend-icon="mdi-magnify"
          clearable
          class="mb-4"
        ></v-text-field>

        <v-tabs v-model="activeCategory" class="mb-4">
          <v-tab>{{ $t('common.all') }}</v-tab>
          <v-tab>{{ $t('documentsRepository.contracts') }}</v-tab>
          <v-tab>{{ $t('documentsRepository.protocols') }}</v-tab>
          <v-tab>{{ $t('documentsRepository.declarations') }}</v-tab>
          <v-tab>{{ $t('documentsRepository.regulations') }}</v-tab>
          <v-tab>{{ $t('documentsRepository.other') }}</v-tab>
        </v-tabs>

        <v-data-table
          :headers="headers"
          :items="filteredDocuments"
          :loading="loading"
          class="elevation-0"
          :search="search"
          :no-data-text="$t('documentsRepository.noDocuments')"
        >
          <template v-slot:[`item.category`]="{ item }">
            <v-chip small :color="getCategoryColor(item.category)" text-color="white">
              {{ item.category }}
            </v-chip>
          </template>
          <template v-slot:[`item.createdAt`]="{ item }">
            {{ new Date(item.createdAt).toLocaleDateString('pl-PL') }}
          </template>
          <template v-slot:[`item.fileSize`]="{ item }">
            {{ formatFileSize(item.fileSize) }}
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn small color="primary" @click="downloadDocument(item)" class="mr-2">
              <v-icon left small>mdi-download</v-icon>
              {{ $t('common.download') }}
            </v-btn>
            <!-- Syntetyczne protokoły z CarHistory NIE są kasowalne z poziomu Repozytorium —
                 to są oryginalne wpisy historii auta. Usuwa się je w panelu auta. -->
            <!-- Dokument, którego nie dało się rozpoznać z maila (np. dwóch
                 kierowców o tym samym nazwisku) — biuro wskazuje osobę ręcznie. -->
            <v-btn v-if="item.kind !== 'rent-protocol'" small color="amber darken-2" dark
              class="mr-2" @click="openAssign(item)" title="Wpisz dokument do teczki kierowcy">
              <v-icon left small>mdi-account-arrow-right-outline</v-icon>
              Przypisz
            </v-btn>
            <v-btn v-if="item.kind !== 'rent-protocol'" small color="error" @click="deleteDocument(item)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
        </template>

        <!-- ========== TAB 2: Podpisane przez kierowców ========== -->
        <template v-if="mainTab === 1 && canAccess('signed-by-drivers')">
          <v-text-field
            v-model="driversSearch"
            :label="$t('documentsRepository.searchDriver')"
            prepend-icon="mdi-magnify"
            clearable
            class="mb-4"
          ></v-text-field>

          <div v-if="loadingSigned" class="text-center pa-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>

          <div v-else-if="filteredDrivers.length === 0" class="text-center pa-8 grey--text">
            <v-icon x-large color="grey lighten-2">mdi-file-search-outline</v-icon>
            <div class="mt-2">{{ $t('documentsRepository.noSignedDocuments') }}</div>
          </div>

          <v-expansion-panels v-else multiple hover>
            <v-expansion-panel
              v-for="d in filteredDrivers"
              :key="d.driverId"
              :disabled="d.totalItems === 0"
            >
              <v-expansion-panel-header>
                <div class="d-flex align-center" style="gap:12px;">
                  <v-avatar size="36" color="blue lighten-4">
                    <span class="blue--text text--darken-3 font-weight-bold">
                      {{ (d.firstName || '?').charAt(0) }}{{ (d.lastName || '').charAt(0) }}
                    </span>
                  </v-avatar>
                  <div>
                    <div class="font-weight-bold">{{ d.firstName }} {{ d.lastName }}</div>
                    <div class="caption grey--text">{{ d.email || d.phone || '—' }}</div>
                  </div>
                  <v-spacer></v-spacer>
                  <v-chip small color="blue lighten-5" text-color="blue darken-3" class="mr-2" v-if="d.documentsCount > 0">
                    <v-icon x-small left>mdi-file-document</v-icon>
                    {{ d.documentsCount }} {{ pluralizeDoc(d.documentsCount) }}
                  </v-chip>
                  <v-chip small color="green lighten-5" text-color="green darken-3" class="mr-2" v-if="d.protocolsCount > 0">
                    <v-icon x-small left>mdi-clipboard-check</v-icon>
                    {{ d.protocolsCount }} {{ pluralizeProtocol(d.protocolsCount) }}
                  </v-chip>
                  <v-chip v-if="d.totalItems === 0" small color="grey lighten-3">brak</v-chip>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th style="width:140px;">{{ $t('documentsRepository.kind') }}</th>
                      <th>{{ $t('documentsRepository.titleDescription') }}</th>
                      <th style="width:200px;">{{ $t('documentsRepository.additional') }}</th>
                      <th style="width:140px;">{{ $t('common.date') }}</th>
                      <th style="width:180px;">{{ $t('documentsRepository.files') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="it in d.items" :key="it.kind + '-' + it.id">
                      <td>
                        <v-chip v-if="it.kind === 'document'" x-small color="blue lighten-5" text-color="blue darken-3" label>
                          <v-icon x-small left>mdi-file-document</v-icon>{{ it.documentType || $t('documentsRepository.documentLabel') }}
                        </v-chip>
                        <v-chip v-else x-small color="green lighten-5" text-color="green darken-3" label>
                          <v-icon x-small left>mdi-clipboard-check</v-icon>{{ it.recordType || $t('documentsRepository.protocolLabel') }}
                        </v-chip>
                      </td>
                      <td>{{ it.title }}</td>
                      <td class="caption">
                        <template v-if="it.kind === 'protocol'">
                          <div v-if="it.car"><v-icon x-small>mdi-car</v-icon> {{ it.car }}</div>
                          <div>
                            <v-icon x-small v-if="it.signedByDriver" color="green" title="Podpis kierowcy">mdi-draw</v-icon>
                            <v-icon x-small v-if="it.signedByAdmin" color="blue" title="Podpis admina" class="ml-1">mdi-draw</v-icon>
                          </div>
                        </template>
                        <template v-else>
                          <v-chip x-small v-if="it.accepted" color="green" text-color="white">{{ $t('documentsRepository.accepted') }}</v-chip>
                          <v-chip x-small v-else color="orange" text-color="white">{{ $t('common.pending') }}</v-chip>
                        </template>
                      </td>
                      <td class="caption">{{ formatDate(it.signedAt) }}</td>
                      <td>
                        <!-- Dla protokołów najmu/zwrotu zawsze pokazujemy przycisk PDF —
                             backend generuje plik na żądanie z danych CarHistory, więc
                             działa też dla starych rekordów bez podpiętego pliku.
                             FORM = legacy typ sprzed wprowadzenia RENT_START. -->
                        <v-btn
                          v-if="it.kind === 'protocol' && (it.recordType === 'RENT_START' || it.recordType === 'RENT_END' || it.recordType === 'FORM')"
                          x-small
                          color="red darken-1"
                          dark
                          @click="downloadRentProtocol(it.id, it.recordType)"
                          class="px-2 mr-1"
                        >
                          <v-icon x-small left>mdi-file-pdf-box</v-icon>
                          Protokół PDF
                        </v-btn>
                        <v-btn
                          v-for="f in (it.files || [])"
                          :key="f.id"
                          x-small
                          text
                          color="primary"
                          @click="downloadFile(f)"
                          class="px-1"
                        >
                          <v-icon x-small left>mdi-download</v-icon>
                          {{ f.orginalName || 'plik' }}
                        </v-btn>
                        <div
                          v-if="(it.files || []).length === 0 && !(it.kind === 'protocol' && (it.recordType === 'RENT_START' || it.recordType === 'RENT_END'))"
                          class="caption grey--text"
                        >—</div>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>

        <!-- ========== TAB 4: Dokumenty wewnętrzne kierowców (tylko biuro) ========== -->
        <internal-documents-section v-if="mainTab === 3" flat @count="internalCount = $event" />

        <!-- ========== TAB 3: Skrzynka mailowa ========== -->
        <template v-if="mainTab === 2">
          <v-alert dense text type="info" class="mb-4">
            Wyślij dokument mailem na podany niżej adres, a system wgra załącznik
            do repozytorium. Sprawdza skrzynkę co 5 minut. Temat maila staje się
            nazwą dokumentu — jeśli zaczyna się od nazwy kategorii (np.
            „Umowy: Kowalski"), dokument trafi właśnie tam.
          </v-alert>

          <v-row dense>
            <v-col cols="12" md="6">
              <div class="text-overline grey--text">Skrzynka</div>
              <v-switch v-model="mailbox.enabled" label="Pobieranie włączone" dense hide-details class="mb-2" />
              <v-text-field v-model="mailbox.user" label="Adres skrzynki (login)" outlined dense hide-details="auto" class="mb-2" placeholder="dokumenty@btpartner.pl" />
              <v-text-field v-model="mailbox.password" type="password" outlined dense hide-details="auto" class="mb-2"
                :label="mailbox.hasPassword ? 'Hasło (zostaw puste, aby nie zmieniać)' : 'Hasło skrzynki'" />
              <v-text-field v-model="mailbox.host" label="Serwer poczty przychodzącej (IMAP)" outlined dense hide-details="auto" class="mb-2" placeholder="imap.nazwa.pl" />
              <v-row dense>
                <v-col cols="6">
                  <v-text-field v-model.number="mailbox.port" label="Port" type="number" outlined dense hide-details="auto" />
                </v-col>
                <v-col cols="6">
                  <v-switch v-model="mailbox.secure" label="Szyfrowane (SSL)" dense hide-details class="mt-1" />
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" md="6">
              <div class="text-overline grey--text">Kto może przysyłać</div>
              <v-text-field v-model="mailbox.allowedDomain" label="Dozwolona domena" outlined dense hide-details="auto" class="mb-2"
                placeholder="btpartner.pl" hint="Każdy adres z tej domeny. Puste = tylko adresy z listy niżej." persistent-hint />
              <v-combobox v-model="mailbox.allowedSenders" label="Dodatkowe adresy spoza domeny" multiple chips small-chips
                outlined dense hide-details="auto" class="mb-2 mt-3" />
              <v-switch v-model="mailbox.requireAuth" dense hide-details class="mb-2"
                label="Wymagaj potwierdzenia nadawcy (SPF/DKIM)" />
              <div class="text-caption grey--text mb-3">
                Adres nadawcy da się w mailu podrobić. Przy włączonym potwierdzeniu
                przyjmujemy tylko listy, którym serwer pocztowy potwierdził nadawcę.
                Wyłączaj tylko świadomie.
              </div>
              <v-text-field v-model="mailbox.defaultCategory" label="Kategoria domyślna" outlined dense hide-details="auto" class="mb-3" />
              <v-switch v-model="mailbox.deleteAfterImport" dense hide-details
                label="Kasuj maile ze skrzynki po wgraniu" />
              <div class="text-caption grey--text">
                Skrzynka ma 20 MB — bez kasowania zapcha się po kilkudziesięciu
                załącznikach i przestanie cokolwiek przyjmować. Dokument i tak zostaje
                w repozytorium, a w dzienniku niżej widać ślad po każdym mailu.
                Listy, przy których wystąpił błąd, zostają w skrzynce.
              </div>
            </v-col>
          </v-row>

          <div class="d-flex align-center mt-4" style="gap: 10px; flex-wrap: wrap;">
            <v-btn color="primary" :loading="mailboxSaving" @click="saveMailbox">
              <v-icon left small>mdi-content-save</v-icon>Zapisz
            </v-btn>
            <v-btn outlined :loading="mailboxChecking" :disabled="!mailbox.enabled" @click="checkMailbox">
              <v-icon left small>mdi-email-sync-outline</v-icon>Sprawdź teraz
            </v-btn>
            <span v-if="mailbox.lastCheckAt" class="text-caption grey--text">
              Ostatnie sprawdzenie: {{ formatDate(mailbox.lastCheckAt) }}
            </span>
            <span v-if="mailbox.lastError" class="text-caption red--text">
              Błąd: {{ mailbox.lastError }}
            </span>
          </div>

          <div class="text-overline grey--text mt-6">Co przyszło na skrzynkę</div>
          <v-data-table
            :headers="mailHeaders"
            :items="mailMessages"
            :items-per-page="15"
            dense
            class="elevation-1"
            no-data-text="Nic jeszcze nie przyszło."
          >
            <template v-slot:[`item.status`]="{ item }">
              <v-chip small :color="mailStatusColor(item.status)" text-color="white">
                {{ mailStatusText(item.status) }}
              </v-chip>
            </template>
            <template v-slot:[`item.receivedAt`]="{ item }">
              {{ formatDate(item.receivedAt) }}
            </template>
            <template v-slot:[`item.attachmentsCount`]="{ item }">
              {{ item.attachmentsCount || '—' }}
            </template>
          </v-data-table>
        </template>
      </v-card-text>
    </v-card>

    <!-- ============ PRZYPISANIE DOKUMENTU DO KIEROWCY ============ -->
    <v-dialog content-class="dark-form-dialog" v-model="assignDialog" max-width="560">
      <v-card v-if="assignDoc">
        <v-card-title class="text-h6 amber--text text--darken-2">
          <v-icon left color="amber darken-2">mdi-account-arrow-right-outline</v-icon>
          Przypisz do kierowcy
        </v-card-title>
        <v-card-text>
          <p class="mb-3">
            <strong>{{ assignDoc.name }}</strong>
            <span class="grey--text"> ({{ assignDoc.originalName }})</span>
          </p>
          <v-autocomplete
            v-model="assignForm.driverId"
            :items="driversList"
            item-text="label"
            item-value="id"
            label="Kierowca"
            outlined dense hide-details="auto" class="mb-3"
            :loading="driversLoading"
            no-data-text="Brak kierowcy o takiej nazwie"
          />
          <v-select
            v-model="assignForm.documentType"
            :items="docTypeOptions"
            label="Typ dokumentu"
            outlined dense hide-details="auto"
          />
          <div class="text-caption grey--text mt-3">
            Dokument trafi do teczki kierowcy ze statusem „oczekuje" i przejdzie
            akceptację tak samo jak wgrany przez niego w aplikacji. Z repozytorium
            zniknie, żeby nie wisiał w dwóch miejscach.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="assignDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="amber darken-2" dark :loading="assigning"
            :disabled="!assignForm.driverId || !assignForm.documentType" @click="assignDocument">
            <v-icon left>mdi-account-arrow-right-outline</v-icon>Przypisz
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Upload Dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="uploadDialog" max-width="600">
      <v-card>
        <v-card-title>{{ $t('documentsRepository.addDocument') }}</v-card-title>
        <v-card-text>
          <v-form ref="uploadForm" v-model="uploadFormValid">
            <v-text-field
              v-model="newDocument.name"
              :label="$t('documentsRepository.documentName')"
              :rules="[v => !!v || $t('documentsRepository.nameRequired')]"
              outlined
            ></v-text-field>
            <v-select
              v-model="newDocument.category"
              :items="categories"
              :label="$t('documentsRepository.category')"
              :rules="[v => !!v || $t('documentsRepository.categoryRequired')]"
              outlined
            ></v-select>
            <v-file-input
              v-model="newDocument.file"
              :label="$t('documentsRepository.file')"
              :rules="[v => !!v || $t('documentsRepository.fileRequired')]"
              outlined
              show-size
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.png"
            ></v-file-input>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="uploadDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" :loading="uploading" @click="uploadDocument">{{ $t('common.add') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { canAccessSubtab } from "@/services/permissions";
import InternalDocumentsSection from "@/components/InternalDocumentsSection.vue";
const axios = require("axios");

export default {
  name: "DocumentsRepositoryPage",
  components: { InternalDocumentsSection },
  data() {
    return {
      loading: false,
      uploading: false,
      downloadingZip: false,
      search: "",
      activeCategory: 0,
      mainTab: 0,
      internalCount: null,
      // Skrzynka mailowa — konfiguracja i dziennik listów.
      mailbox: {
        enabled: false, host: "", port: 993, secure: true, user: "", password: "",
        allowedDomain: "btpartner.pl", allowedSenders: [], requireAuth: true,
        defaultCategory: "Skrzynka", deleteAfterImport: true,
        hasPassword: false, lastCheckAt: null, lastError: null,
      },
      // Ręczne przypisanie dokumentu do teczki kierowcy.
      assignDialog: false,
      assignDoc: null,
      assigning: false,
      assignForm: { driverId: null, documentType: null },
      driversList: [],
      driversLoading: false,
      docTypeOptions: [
        { text: 'Karta pobytu (przód)', value: 'RESIDENCE_CARD_FRONT' },
        { text: 'Karta pobytu (tył)', value: 'RESIDENCE_CARD_BACK' },
        { text: 'Prawo jazdy (przód)', value: 'DRIVING_LICENSE' },
        { text: 'Prawo jazdy (tył)', value: 'DRIVING_LICENSE_BACK' },
        { text: 'Dowód rejestracyjny (przód)', value: 'VEHICLE_REG_FRONT' },
        { text: 'Dowód rejestracyjny (tył)', value: 'VEHICLE_REG_BACK' },
        { text: 'Zdjęcie pojazdu (przód)', value: 'VEHICLE_PHOTO_FRONT' },
        { text: 'Zdjęcie pojazdu (tył)', value: 'VEHICLE_PHOTO_BACK' },
        { text: 'Ubezpieczenie OC', value: 'INSURANCE_OC' },
        { text: 'Badanie psychotechniczne', value: 'PSYCHO_CERT' },
        { text: 'Badanie lekarskie', value: 'MEDICAL_CERT' },
        { text: 'Zaświadczenie o niekaralności (PL)', value: 'CRIMINAL_RECORD' },
        { text: 'Zaświadczenie o niekaralności (zagraniczne)', value: 'CRIMINAL_RECORD_FOREIGN' },
        { text: 'Tłumaczenie zaświadczenia o niekaralności', value: 'CRIMINAL_RECORD_TRANSLATION' },
        { text: 'Legitymacja studencka (przód)', value: 'STUDENT_ID_FRONT' },
        { text: 'Legitymacja studencka (tył)', value: 'STUDENT_ID_BACK' },
        { text: 'Zaświadczenie o emeryturze', value: 'PENSION_CERT' },
        { text: 'Orzeczenie o niepełnosprawności', value: 'DISABILITY_CERT' },
        { text: 'Zaświadczenie o rencie', value: 'RENT_CERT' },
      ],
      mailboxSaving: false,
      mailboxChecking: false,
      mailMessages: [],
      mailHeaders: [
        { text: "Kiedy", value: "receivedAt" },
        { text: "Od kogo", value: "fromAddress" },
        { text: "Temat", value: "subject" },
        { text: "Załączników", value: "attachmentsCount" },
        { text: "Wynik", value: "status" },
        { text: "Szczegóły", value: "reason" },
      ],
      uploadDialog: false,
      uploadFormValid: true,
      documents: [],
      // Podpisane przez kierowców
      loadingSigned: false,
      driversSearch: "",
      signedByDrivers: [],
      categories: [
        "Umowy",
        "Protokoły",
        "Oświadczenia",
        "Regulaminy",
        "Inne",
      ],
      headers: [
        { text: "Nazwa", value: "name" },
        { text: "Kategoria", value: "category" },
        { text: "Typ pliku", value: "fileType" },
        { text: "Rozmiar", value: "fileSize" },
        { text: "Data dodania", value: "createdAt" },
        { text: "Akcje", value: "actions", sortable: false },
      ],
      newDocument: {
        name: "",
        category: null,
        file: null,
      },
    };
  },
  computed: {
    filteredDocuments() {
      if (this.activeCategory === 0) return this.documents;
      const categoryMap = { 1: "Umowy", 2: "Protokoły", 3: "Oświadczenia", 4: "Regulaminy", 5: "Inne" };
      const cat = categoryMap[this.activeCategory];
      if (cat === "Inne") {
        return this.documents.filter(
          (d) => !["Umowy", "Protokoły", "Oświadczenia", "Regulaminy"].includes(d.category)
        );
      }
      return this.documents.filter((d) => d.category === cat);
    },
    signedDriversTotal() {
      return this.signedByDrivers.reduce((acc, d) => acc + (d.totalItems || 0), 0);
    },
    filteredDrivers() {
      const q = (this.driversSearch || "").toLowerCase().trim();
      if (!q) return this.signedByDrivers;
      return this.signedByDrivers.filter(
        (d) =>
          (d.firstName || "").toLowerCase().includes(q) ||
          (d.lastName || "").toLowerCase().includes(q) ||
          (d.email || "").toLowerCase().includes(q) ||
          (d.phone || "").toLowerCase().includes(q),
      );
    },
  },
  watch: {
    mainTab(val) {
      if (val === 1 && this.signedByDrivers.length === 0) this.fetchSignedByDrivers();
      if (val === 2) this.fetchMailbox();
    },
  },
  async created() {
    await this.fetchDocuments();
  },
  methods: {
    canAccess(sub) { return canAccessSubtab('documents-repository', sub); },

    // ── PRZYPISANIE DOKUMENTU DO TECZKI KIEROWCY ──
    async openAssign(doc) {
      this.assignDoc = doc;
      this.assignForm = { driverId: null, documentType: null };
      this.assignDialog = true;
      if (this.driversList.length) return;
      this.driversLoading = true;
      try {
        const r = await axios.get(
          process.env.VUE_APP_ROOT_API + 'drivers?take=10000&skip=1&search=',
          { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } },
        );
        this.driversList = (r.data?.data?.result || [])
          .map((d) => ({ id: d.id, label: `${d.firstName || ''} ${d.lastName || ''}`.trim() || d.email }))
          .sort((a, b) => a.label.localeCompare(b.label, 'pl'));
      } catch (e) {
        console.error('Nie wczytano kierowców:', e);
      }
      this.driversLoading = false;
    },
    async assignDocument() {
      this.assigning = true;
      try {
        const { data } = await axios.post(
          process.env.VUE_APP_ROOT_API + 'documents-repository/' + this.assignDoc.id + '/assign-driver',
          this.assignForm,
          { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } },
        );
        Swal.fire('Przypisano', data?.data?.message || 'Dokument trafił do teczki kierowcy.', 'success');
        this.assignDialog = false;
        await this.fetchDocuments();
      } catch (e) {
        Swal.fire('Błąd', e?.response?.data?.message?.toString?.() || 'Nie przypisano', 'error');
      }
      this.assigning = false;
    },

    // ── SKRZYNKA MAILOWA ──
    mailApi(path) { return process.env.VUE_APP_ROOT_API + "inbound-mail" + path; },
    mailHeadersAuth() { return { Authorization: "Bearer " + localStorage.getItem("token") }; },
    async fetchMailbox() {
      try {
        const [cfg, msgs] = await Promise.all([
          axios.get(this.mailApi("/config"), { headers: this.mailHeadersAuth() }),
          axios.get(this.mailApi("/messages"), { headers: this.mailHeadersAuth() }),
        ]);
        const d = cfg.data?.data;
        // Hasła backend nie zwraca — puste pole znaczy „nie zmieniaj".
        if (d) this.mailbox = { ...this.mailbox, ...d, password: "" };
        this.mailMessages = msgs.data?.data || [];
      } catch (e) {
        console.error("Nie wczytano skrzynki:", e);
      }
    },
    async saveMailbox() {
      this.mailboxSaving = true;
      try {
        const { data } = await axios.post(this.mailApi("/config"), this.mailbox, {
          headers: this.mailHeadersAuth(),
        });
        this.mailbox = { ...this.mailbox, ...(data?.data || {}), password: "" };
        Swal.fire("Zapisano", "Ustawienia skrzynki zapisane.", "success");
      } catch (e) {
        Swal.fire("Błąd", e?.response?.data?.message?.toString?.() || "Nie zapisano", "error");
      }
      this.mailboxSaving = false;
    },
    async checkMailbox() {
      this.mailboxChecking = true;
      try {
        const { data } = await axios.post(this.mailApi("/check"), {}, { headers: this.mailHeadersAuth() });
        const r = data?.data || {};
        const tresc = r.error
          ? `Nie udało się połączyć: ${r.error}`
          : r.skipped
            ? `Pominięto — ${r.reason}`
            : `Sprawdzono listów: ${r.sprawdzone || 0}, przyjęto: ${r.przyjete || 0}, odrzucono: ${r.odrzucone || 0}` +
              (r.skasowane ? `, skasowano ze skrzynki: ${r.skasowane}` : '');
        Swal.fire(r.error ? "Błąd" : "Sprawdzone", tresc, r.error ? "error" : "success");
        await this.fetchMailbox();
        await this.fetchDocuments();
      } catch (e) {
        Swal.fire("Błąd", e?.response?.data?.message?.toString?.() || "Nie sprawdzono", "error");
      }
      this.mailboxChecking = false;
    },
    mailStatusColor(s) {
      return { ACCEPTED: "green", REJECTED_SENDER: "red", REJECTED_AUTH: "deep-orange",
        NO_ATTACHMENT: "grey", ERROR: "red darken-3" }[s] || "grey";
    },
    mailStatusText(s) {
      return { ACCEPTED: "Wgrane", REJECTED_SENDER: "Obcy nadawca",
        REJECTED_AUTH: "Niepotwierdzony nadawca", NO_ATTACHMENT: "Bez załącznika",
        ERROR: "Błąd" }[s] || s;
    },
    getCategoryColor(category) {
      const colors = {
        Umowy: "blue",
        "Protokoły": "green",
        "Oświadczenia": "orange",
        Regulaminy: "purple",
        Inne: "grey",
      };
      return colors[category] || "grey";
    },
    formatFileSize(size) {
      if (!size) return "-";
      const num = parseInt(size);
      if (num < 1024) return num + " B";
      if (num < 1024 * 1024) return (num / 1024).toFixed(1) + " KB";
      return (num / (1024 * 1024)).toFixed(1) + " MB";
    },
    async fetchSignedByDrivers() {
      this.loadingSigned = true;
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "documents-repository/signed-by-drivers",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.signedByDrivers = res.data?.data || [];
      } catch (e) {
        console.log(e);
        this.signedByDrivers = [];
      } finally {
        this.loadingSigned = false;
      }
    },
    // Otwórz PDF protokołu najmu (Tab 2 — Podpisane przez kierowców).
    // Backend regeneruje PDF z carHistory na żądanie.
    async downloadRentProtocol(historyId, recordType) {
      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "cars-history/" + historyId + "/protocol-pdf",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          responseType: "blob",
        });
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      } catch (e) {
        console.error("downloadRentProtocol:", e);
        let msg = recordType === 'RENT_END'
          ? "Nie udało się wygenerować protokołu zwrotu"
          : "Nie udało się wygenerować protokołu najmu";
        try {
          const blob = e?.response?.data;
          if (blob && typeof blob.text === "function") {
            const text = await blob.text();
            try {
              const parsed = JSON.parse(text);
              msg = parsed?.message || parsed?.error || text || msg;
            } catch {
              if (text) msg = text;
            }
          }
        } catch { /* keep default */ }
        Swal.fire("Błąd protokołu", msg, "error");
      }
    },
    async downloadFile(file) {
      try {
        const res = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "aws/download-file/" + file.id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          responseType: "blob",
        });
        const blob = new Blob([res.data]);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.orginalName || "plik";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", "Nie udało się pobrać pliku", "error");
      }
    },
    formatDate(d) {
      if (!d) return "—";
      const dt = new Date(d);
      return (
        dt.getDate().toString().padStart(2, "0") + "." +
        (dt.getMonth() + 1).toString().padStart(2, "0") + "." +
        dt.getFullYear() + " " +
        dt.getHours().toString().padStart(2, "0") + ":" +
        dt.getMinutes().toString().padStart(2, "0")
      );
    },
    pluralizeDoc(n) {
      if (n === 1) return "dokument";
      if (n >= 2 && n <= 4) return "dokumenty";
      return "dokumentów";
    },
    pluralizeProtocol(n) {
      if (n === 1) return "protokół";
      if (n >= 2 && n <= 4) return "protokoły";
      return "protokołów";
    },
    async fetchDocuments() {
      this.loading = true;
      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "documents-repository",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.documents = response.data?.data || [];
      } catch (e) {
        console.log(e);
        this.documents = [];
      } finally {
        this.loading = false;
      }
    },
    async uploadDocument() {
      if (!this.$refs.uploadForm.validate()) return;

      this.uploading = true;
      try {
        const formData = new FormData();
        formData.append("name", this.newDocument.name);
        formData.append("category", this.newDocument.category);
        formData.append("file", this.newDocument.file);

        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "documents-repository",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
          data: formData,
        });

        this.uploadDialog = false;
        this.newDocument = { name: "", category: null, file: null };
        await this.fetchDocuments();
        Swal.fire("Sukces", "Dokument został dodany.", "success");
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message || "Wystąpił błąd", "error");
      } finally {
        this.uploading = false;
      }
    },
    async downloadZip() {
      this.downloadingZip = true;
      try {
        const categoryMap = { 1: "Umowy", 2: "Protokoły", 3: "Oświadczenia", 4: "Regulaminy", 5: "Inne" };
        const cat = categoryMap[this.activeCategory];
        const url =
          process.env.VUE_APP_ROOT_API +
          "documents-repository/download-all" +
          (cat ? "?category=" + encodeURIComponent(cat) : "");
        const res = await axios({
          method: "get",
          url,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          responseType: "blob",
        });
        const blob = new Blob([res.data], { type: "application/zip" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        const datePart = new Date().toISOString().split("T")[0];
        link.download = (cat ? "dokumenty-" + cat : "dokumenty") + "-" + datePart + ".zip";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      } catch (e) {
        let msg = "Nie udało się pobrać dokumentów";
        try {
          if (e.response?.data) {
            const text = await e.response.data.text();
            msg = JSON.parse(text).message || msg;
          }
        } catch (_) { /* ignore */ }
        Swal.fire("Błąd", msg, "error");
      }
      this.downloadingZip = false;
    },
    async downloadDocument(doc) {
      try {
        // Syntetyczny protokół najmu — backend generuje PDF na żądanie
        // z rekordu CarHistory, nie ma fileUrl.
        if (doc.kind === 'rent-protocol') {
          const response = await axios({
            method: "get",
            url: process.env.VUE_APP_ROOT_API + "cars-history/" + doc.id + "/protocol-pdf",
            headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            responseType: "blob",
          });
          const blob = new Blob([response.data], { type: "application/pdf" });
          const url = window.URL.createObjectURL(blob);
          window.open(url, "_blank");
          setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
          return;
        }
        // fileUrl to klucz w prywatnym S3, nie adres — plik idzie przez backend.
        if (doc.fileUrl) {
          const okno = window.open("", "_blank");
          const response = await axios({
            method: "get",
            url: process.env.VUE_APP_ROOT_API + "documents-repository/" + doc.id + "/download",
            headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            responseType: "blob",
          }).catch((e) => { if (okno) okno.close(); throw e; });
          const typ = doc.fileType || response.data.type;
          const url = window.URL.createObjectURL(new Blob([response.data], { type: typ }));
          if (okno && /^(application\/pdf|image\/|text\/plain)/.test(typ)) {
            okno.location.href = url;
          } else {
            if (okno) okno.close();
            const a = document.createElement("a");
            a.href = url;
            a.download = doc.originalName || doc.name || "plik";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }
          setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        }
      } catch (e) {
        console.log("downloadDocument error:", e);
        // Axios z responseType:"blob" zwraca blob też przy 4xx/5xx. Musimy
        // go odczytać jako tekst żeby dostać prawdziwy komunikat błędu z backendu,
        // inaczej leciał generyczny "Nie udało się pobrać".
        let msg = "Nie udało się pobrać dokumentu";
        try {
          const blob = e?.response?.data;
          if (blob && typeof blob.text === "function") {
            const text = await blob.text();
            try {
              const parsed = JSON.parse(text);
              msg = parsed?.message || parsed?.error || text || msg;
            } catch {
              if (text) msg = text;
            }
          } else if (e?.response?.data?.message) {
            msg = e.response.data.message;
          } else if (e?.message) {
            msg = e.message;
          }
        } catch { /* keep default */ }
        Swal.fire("Błąd pobierania", msg, "error");
      }
    },
    async deleteDocument(doc) {
      const result = await Swal.fire({
        title: "Usunąć dokument?",
        text: doc.name,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Tak, usuń",
        cancelButtonText: "Anuluj",
      });
      if (!result.isConfirmed) return;

      try {
        await axios({
          method: "delete",
          url: process.env.VUE_APP_ROOT_API + "documents-repository/" + doc.id,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        await this.fetchDocuments();
        Swal.fire("Usunięto", "Dokument został usunięty.", "success");
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message || "Wystąpił błąd", "error");
      }
    },
  },
};
</script>

<style scoped>
.docrepo-page { background: #181C28; min-height: calc(100vh - 56px); padding: 16px 16px 40px; margin: -24px; color: #fff; }
.page-header { display: flex; align-items: center; gap: 12px; padding: 4px 4px 18px; }
.page-avatar { width: 44px; height: 44px; border-radius: 14px; background: linear-gradient(135deg, #2962FF 0%, #5478FF 100%); display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 6px 18px rgba(41,98,255,0.4); }
.page-header-text { flex: 1; }
.page-eyebrow { font-size: 12px; color: #6E7691; font-weight: 500; }
.page-title { font-size: 22px; font-weight: 800; color: #fff; margin: 2px 0 0; letter-spacing: -0.02em; }
.action-pill { display: inline-flex; align-items: center; gap: 6px; background: #2962FF; border: 1px solid #2962FF; color: #fff; padding: 8px 14px; border-radius: 10px; font-size: 12.5px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 12px rgba(41,98,255,0.3); transition: transform 0.12s ease; font-family: inherit; }
.action-pill:hover { transform: translateY(-1px); }
.action-pill .v-icon { color: inherit !important; }

.docrepo-page >>> .v-card { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; box-shadow: none !important; color: #fff !important; }
.docrepo-page >>> .v-card .v-card__title { color: #fff !important; padding: 16px 20px 8px !important; font-weight: 700 !important; font-size: 15px !important; }
.docrepo-page >>> .v-text-field--outlined fieldset, .docrepo-page >>> .v-select--outlined fieldset { border-color: rgba(255,255,255,0.1) !important; }
.docrepo-page >>> .v-text-field--outlined .v-input__slot, .docrepo-page >>> .v-select--outlined .v-input__slot { background: #181C28 !important; border-radius: 12px !important; }
.docrepo-page >>> .v-label { color: #6E7691 !important; }
.docrepo-page >>> .v-text-field input, .docrepo-page >>> .v-select__selection { color: #fff !important; }
.docrepo-page >>> .v-data-table { background: transparent !important; color: #fff !important; }
.docrepo-page >>> .v-data-table thead tr th { background: #1F2434 !important; color: #6E7691 !important; font-size: 11px !important; font-weight: 700 !important; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid rgba(255,255,255,0.06) !important; }
.docrepo-page >>> .v-data-table tbody tr { background: transparent !important; }
.docrepo-page >>> .v-data-table tbody tr:hover { background: #2A3042 !important; }
.docrepo-page >>> .v-data-table tbody tr td { color: #fff !important; border-bottom: 1px solid rgba(255,255,255,0.04) !important; }
.docrepo-page >>> .v-data-footer { background: #1F2434 !important; color: #B0BEC5 !important; border-top: 1px solid rgba(255,255,255,0.06) !important; }
.docrepo-page >>> .v-tabs { background: transparent !important; }
.docrepo-page >>> .v-tab { color: #B0BEC5 !important; text-transform: none !important; font-weight: 600 !important; }
.docrepo-page >>> .v-tab--active { color: #5478FF !important; }
.docrepo-page >>> .v-tabs-slider { background: #5478FF !important; }
.docrepo-page >>> .v-tabs-bar { background: transparent !important; }
.docrepo-page >>> .v-tabs-items, .docrepo-page >>> .v-window { background: transparent !important; }
.docrepo-page >>> .v-btn:not(.v-btn--icon):not(.action-pill) { border-radius: 10px !important; text-transform: none !important; font-weight: 600 !important; }
.docrepo-page >>> .v-chip { border-radius: 999px !important; font-weight: 700 !important; }
.docrepo-page >>> .v-divider { border-color: rgba(255,255,255,0.06) !important; }
.docrepo-page >>> .v-progress-linear { background: rgba(255,255,255,0.06) !important; }
.docrepo-page >>> .v-dialog .v-card { background: #232838 !important; }
</style>
